<script lang="ts">
import { defineComponent, Ref, ref } from 'vue';
import { PageStore } from 'stores/Page/Page.ts';
import { storeToRefs } from 'pinia';
import { AttributeValueWebItem, AttributeWebItem, ProductCategoryItem, ProductItem } from 'src/core/types/Products';
import { useMeta, useQuasar } from 'quasar';
import ProductCard from 'components/Product/Card/ProductCard.vue';
import { useRoute } from 'vue-router';

interface ProductAttributes extends AttributeWebItem {
  items: AttributeValueWebItem;
}

interface CategoryContent {
  productList: ProductItem[];
  categoryDetail: ProductCategoryItem;
  productCategories: ProductCategoryItem[];
  productAttributes: ProductAttributes[];
}

export default defineComponent({
  name: 'ProductsPage',

  components: { ProductCard },

  computed: {
    searchParam() {
      return this.$route.query.s;
    },
  },

  setup() {
    const $route = useRoute();
    const $q = useQuasar();

    const pageStore = PageStore();
    const { head, content }: { head: any; content: Ref<CategoryContent> } = storeToRefs(pageStore);

    const siteAddress = head.value.title.siteAddress.endsWith('/') ? head.value.title.siteAddress.slice(0, -1) : head.value.title.siteAddress;
    
    useMeta({
      titleTemplate: (title) => `${title} | ${content.value.categoryDetail.name}`,
      link: {
        canonical: { rel: 'canonical', href: `${siteAddress}/${content.value.categoryDetail.link}` },
      },
      script: {
        schemaCollectionPage: {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${head.value.title.siteTitle} - ${content.value.categoryDetail.name}`,
            description: content.value.categoryDetail.desc,
            url: `${siteAddress}/${content.value.categoryDetail.link}`,
            mainEntity: {
              '@type': 'ItemList',
              itemListElement: content.value.productList.map((product, index) => {
                const offers = [];
                product.variations.forEach((variation) => {
                  const price = variation.priceNumber || 0;
                  const inStock = variation.stock > 0;

                  offers.push({
                    '@type': 'Offer',
                    priceCurrency: 'TRY',
                    price: price.toFixed(2),
                    availability: inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
                  });
                });
                const firstVariation = product.variations?.[0] || {};
                const image = firstVariation.images?.[0] || '';

                return {
                  '@type': 'ListItem',
                  position: index + 1,
                  item: {
                    '@type': 'Product',
                    name: product.name,
                    image: image,
                    url: `${siteAddress}/${product.link}`,
                    sku: product.kod,
                    category: product.category?.name,
                    offers,
                  },
                };
              }),
            },
          }),
        },
      },
    });

    const selectedCategory = ref([]);

    const attributes = ref(content.value.productAttributes);

    const selectedAttributes = ref<{ [key: string]: string[] }>({});

    attributes.value.forEach((attr) => {
      selectedAttributes.value[attr.code] = [];
    });

    if ($route.query.a) {
      const searchAttrs = $route.query.a.toString().split(',');
      attributes.value.forEach((attr) => {
        attr.items.forEach((item) => {
          if (searchAttrs.includes(item.code.toString())) {
            selectedAttributes.value[attr.code].push(item);
          }
        });
      });
    }

    // Nitelik seçimi güncelleme
    const updateSelection = (attrCode: number, value: string) => {
      if (!selectedAttributes.value[attrCode]) {
        selectedAttributes.value[attrCode] = [];
      }

      const index = selectedAttributes.value[attrCode].indexOf(value);
      if (index === -1) {
        selectedAttributes.value[attrCode].push(value);
      } else {
        selectedAttributes.value[attrCode].splice(index, 1);
      }
    };

    // Nitelik seçimi güncelleme
    const updateCatSelection = (value: ProductCategoryItem) => {
      const index = selectedAttributes.value.indexOf(value);
      if (index === -1) {
        selectedAttributes.value.push(value);
      } else {
        selectedAttributes.value.splice(index, 1);
      }
    };

    selectedCategory.value.push(content.value.productCategories.find((o) => o.code === content.value.categoryDetail.code));

    const search = ref($route.query.s || '');

    const chunkSize = 2;
    return {
      $q,
      search,
      category: content.value.categoryDetail,
      productList: content.value.productList,
      categories: content.value.productCategories,
      selectedCategory,
      attributes: attributes.value.reduce((acc, curr, index) => {
        if (index % 6 === 0) acc.push([]);
        acc[acc.length - 1].push(curr);
        return acc;
      }, []),
      chunkSize,
      selectedAttributes,
      updateSelection,
      updateCatSelection,
    };
  },

  methods: {
    setQueryState(key: string, value: string) {
      const params = new URLSearchParams(window.location.search);
      params.set(key, value);
      window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    },

    resetSelectedAttributes() {
      Object.keys(this.selectedAttributes).forEach((attrCode) => {
        this.selectedAttributes[attrCode] = [];
      });
    },
  },

  watch: {
    selectedAttributes: {
      handler() {
        Object.keys(this.selectedAttributes).forEach((attrCode) => {
          if (this.selectedAttributes[attrCode] === null) {
            this.selectedAttributes[attrCode] = [];
          }
        });

        const entries = Object.values(this.selectedAttributes)
          .flat()
          .map((item) => item.code);

        this.setQueryState('a', entries.join(','));
      },
      deep: true,
    },
  },
});
</script>

<template>
  <q-page>
    <div class="container">
      <div class="row">
        <div class="col-12" :class="$q.platform.is.mobile ? 'q-px-md' : null">
          <h1 class="text-h4 q-my-sm">{{ category.name }}</h1>
          <p class="q-mb-md" style="margin-left: 2px">{{ category.desc }}</p>
        </div>
        <div class="col-12 q-mb-md">
          <q-card class="bg-transparent no-box-shadow q-px-none">
            <div>
              <!-- Nitelikler Listesi -->
              <q-card-section class="q-px-none">
                <div :class="$q.platform.is.mobile ? 'q-px-md' : null">
                  <div class="flex items-center q-mb-md">
                    <div class="text-body1 q-mr-md">
                      <q-icon name="filter_alt" size="1rem" />
                      Filtrele
                    </div>
                    <div>
                      <q-btn color="grey-4" text-color="grey-10" dense size=".8rem" class="q-px-md" @click="resetSelectedAttributes" unelevated no-caps>
                        Filterleri Sıfırla
                      </q-btn>
                    </div>
                  </div>
                  <div v-for="(attrCol, key) in attributes" :key="key" :model-value="true" class="row q-col-gutter-md q-mb-md">
                    <!--                        <q-checkbox-->
                    <!--                          :label="value.name"-->
                    <!--                          :val="value.code"-->
                    <!--                          v-model="selectedAttributes[attr.code]"-->
                    <!--                          @update:model-value="updateSelection(attr.code, value)"-->
                    <!--                          dense-->
                    <!--                        />-->
                    <div class="col-6 col-md-3 col-lg-2 column items-stretch" :key="attr.code" v-for="attr in attrCol">
                      <q-select
                        filled
                        v-model="selectedAttributes[attr.code]"
                        use-input
                        input-debounce="0"
                        :label="attr.name"
                        :options="attr.items"
                        option-value="code"
                        option-label="name"
                        @filter="filterFn"
                        style="width: 100%"
                        :behavior="$q.platform.is.mobile ? 'dialog' : 'default'"
                        multiple
                        use-chips
                        dense
                      >
                        <template #selected>
                          <div v-if="selectedAttributes[attr.code].length" class="text-weight-medium q-py-xs">
                            <q-badge rounded>{{ selectedAttributes[attr.code].length }}</q-badge>
                            Seçili
                          </div>
                        </template>
                        <template #option="scope">
                          <q-item v-bind="scope.itemProps">
                            <q-item-section side>
                              <q-checkbox size="1.5rem" :val="scope.opt" v-model="selectedAttributes[attr.code]" />
                            </q-item-section>
                            <q-item-section>
                              {{ scope.opt.name }}
                            </q-item-section>
                          </q-item>
                        </template>
                        <template v-slot:no-option>
                          <q-item>
                            <q-item-section class="text-grey"> Sonuç Yok</q-item-section>
                          </q-item>
                        </template>
                        <template #after-options>
                          <div class="column">
                            <q-btn color="primary" class="q-py-sm" dense v-close-popup no-caps>
                              <q-icon name="filter_list" size="1.2rem" class="q-mr-sm" />
                              Ürünleri Filtrele
                            </q-btn>
                          </div>
                        </template>
                      </q-select>
                    </div>
                  </div>
                </div>
              </q-card-section>
            </div>
          </q-card>
        </div>
        <div class="col-12 q-px-lg">
          <div class="row q-col-gutter-md">
            <div :key="productKey" v-for="(product, productKey) in productList" class="col-12 col-sm-6 col-lg-3 q-mb-md">
              <product-card :item="product" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </q-page>
</template>

<style scoped>
.product-search--category-chip {
  cursor: pointer;
}
</style>
